import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '../services/api';

// Token storage key
const TOKEN_KEY = 'auth_token';

// Decode JWT payload without verifying signature (read-only, client-side display)
function decodeTokenPayload(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

// Basic XSS sanitization for token
function sanitizeToken(token) {
  if (!token || typeof token !== 'string') return null;
  // Remove any potentially dangerous characters
  return token.replace(/[<>'"]/g, '');
}

// Secure token storage with basic validation
function getStoredToken() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    // Basic JWT structure validation (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }

    return token;
  } catch {
    return null;
  }
}

function setStoredToken(token) {
  const sanitized = sanitizeToken(token);
  if (sanitized) {
    localStorage.setItem(TOKEN_KEY, sanitized);
  }
}

function removeStoredToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref(getStoredToken());
  const user = ref(null);
  const loading = ref(false);
  const error = ref(null);

  // Getters
  const isAuthenticated = computed(() => !!token.value);
  const getToken = computed(() => token.value);

  // Actions
  async function login(username, password) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/login', { username, password });
      const newToken = response.data.data.token;

      if (!newToken) {
        throw new Error('No token received from server');
      }

      token.value = newToken;
      setStoredToken(newToken);
      user.value = decodeTokenPayload(newToken);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Login failed';
      error.value = message;
      return { success: false, error: message };
    } finally {
      loading.value = false;
    }
  }

  async function register(username, password) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/register', { username, password });
      return { success: true, data: response.data };
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Registration failed';
      error.value = message;
      return { success: false, error: message };
    } finally {
      loading.value = false;
    }
  }

  async function requestPasswordReset(username) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/reset-password', { username });
      return { success: true, data: response.data };
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Password reset request failed';
      error.value = message;
      return { success: false, error: message };
    } finally {
      loading.value = false;
    }
  }

  async function confirmPasswordReset(username, resetToken, newPassword) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/reset-password/confirm', {
        username,
        reset_token: resetToken,
        new_password: newPassword
      });
      return { success: true, data: response.data };
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Password reset failed';
      error.value = message;
      return { success: false, error: message };
    } finally {
      loading.value = false;
    }
  }

  function logout() {
    token.value = null;
    user.value = null;
    error.value = null;
    removeStoredToken();
  }

  function clearError() {
    error.value = null;
  }

  // Initialize token in axios on store creation
  function initializeAuth() {
    const storedToken = getStoredToken();
    if (storedToken) {
      token.value = storedToken;
      user.value = decodeTokenPayload(storedToken);
    }
  }

  return {
    // State
    token,
    user,
    loading,
    error,
    // Getters
    isAuthenticated,
    getToken,
    // Actions
    login,
    register,
    requestPasswordReset,
    confirmPasswordReset,
    logout,
    clearError,
    initializeAuth
  };
});
